import React from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";

const wrap = { minHeight:"60vh", display:"flex", alignItems:"center", justifyContent:"center", padding:16 };
const card = { width:"100%", maxWidth:960, background:"#fff", borderRadius:16, boxShadow:"0 8px 18px rgba(0,0,0,0.08)", padding:24 };
const grid = { display:"grid", gap:16, gridTemplateColumns:"1fr", alignItems:"start" };
const gridLg = { ...grid, gridTemplateColumns:"1fr 1fr" };
const imgBox = { width:"100%", borderRadius:12, overflow:"hidden", background:"#f3f4f6" };
const imgStyle = { width:"100%", height:400, objectFit:"cover" };
const text = { fontSize:14, color:"#374151" };
const title = { fontSize:20, fontWeight:600, color:"#111827", margin:"0 0 8px" };

// Normalize any absolute media URL to a relative one so CRA proxy handles it.
// Works for http(s)://host/media/..., bare "media/...", "./media/...", or already "/media/..."
function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/media/")) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    // strip scheme+host; keep path/query/hash
    return url.replace(/^https?:\/\/[^/]+/, "");
  }
  const trimmed = url.replace(/^\.?\/*/, ""); // remove leading "./" or "/"
  return trimmed.startsWith("media/") ? `/${trimmed}` : `/media/${trimmed}`;
}


function resolveImg(src) {
  if (!src) return null;
  try { new URL(src); return src; } catch {}
  const MEDIA_BASE = process.env.REACT_APP_MEDIA_BASE || (api.base || "");
  const needsSlash = src[0] !== "/";
  return `${MEDIA_BASE}${needsSlash ? "/" : ""}${src}`;
}

export default function ArtworkDetail() {
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = React.useState(true);
  const [artwork, setArtwork] = React.useState(null);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getJson(`/artworks/${id}/`);
        if (!cancelled) setArtwork(data);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div style={wrap}><div style={card}><div style={text}>Loading artwork…</div></div></div>
    );
  }
  if (error || !artwork) {
    return (
      <div style={wrap}>
        <div style={card}>
          <div style={{...text, color:"#b91c1c"}}>Failed to load artwork{error ? `: ${error}` : ""}</div>
          <div style={{marginTop:12}}><Link to="/" style={{textDecoration:"underline"}}>Go home</Link></div>
        </div>
      </div>
    );
  }

  const hero =
    artwork.image_url ??
    artwork.image ??
    (Array.isArray(artwork.images) ? artwork.images[0]?.image : null);
  const heroSrc = resolveImg(hero);

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
          <div style={{ ...title, margin: 0 }}>{artwork.title || "Untitled"}</div>
          <Link
            to="/help"
            state={{ backgroundLocation: location, helpSection: "discovering-art" }}
            style={{
              width: 34,
              height: 34,
              borderRadius: "999px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,0.9)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
              color: "#111827",
              textDecoration: "none",
              fontWeight: 800,
              flexShrink: 0,
            }}
            aria-label="Help for artwork details"
          >
            ?
          </Link>
        </div>
        <div style={window.innerWidth >= 1024 ? gridLg : grid}>
          <div style={imgBox}>
            {heroSrc ? <img src={toRelativeMedia(heroSrc)} alt={artwork.title} style={imgStyle} /> : <div style={{...imgStyle, background:"#e5e7eb"}}/>}
          </div>
          <div>
            <p style={text}>{artwork.description || "No description."}</p>
            <div style={{...text, marginTop:12}}><strong>Price:</strong> {artwork.price ?? "—"}</div>
            <div style={{...text, marginTop:4}}><strong>Type:</strong> {artwork.artwork_type ?? "—"}</div>
            {/* add any other fields you expose */}
            <div style={{marginTop:16}}>
              <Link to="/buyers/purchases" style={{textDecoration:"underline"}}>Back to My Purchases</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
